import type { PressureContext, RubricDimension, Track } from "@pushback-and-chaos/contracts";
import { GOAL_LABEL, LIBRARY, SKILL_LABEL, TRACKS } from "../design/copy.js";

/**
 * THE SHELVES (library pass, 2026-09-08): which rows the library shows, in
 * what order, holding which cases. Pure — a function of the published cases
 * and the learner's own state, with no DOM and no clock — so the whole
 * arrangement is a unit test.
 *
 * The rows come from the story axes the case bible already declares: the
 * track, the primary rubric dimension (the card's `skill:`), and the
 * pressure context (the card's `goal:`) grouped into three kinds of stake.
 * Nothing is invented for the library that the authoring pipeline does not
 * already know about a case.
 *
 * ORDER. Calibration first while the library is locked — it is the only open
 * row — and directly after the coach's row once it is not. Every other row
 * is locked until three calibration engagements are finished and the first
 * development plan is read. A locked row is still shown, greyed, with the
 * one sentence that says what opens it: a padlock says "withheld", a sentence
 * says "here is the door".
 *
 * THE COACH'S ROW is derived, not modelled: cases that train the dimension
 * the learner's own record says is furthest behind, then the dimensions the
 * plan's next steps name, minus anything already finished. It is the same
 * arithmetic the practice record does for its shape paragraph — no model is
 * called to shelve a card, and the row cannot say anything the record has
 * not already said.
 */
export interface ShelfCase {
  readonly slug: string;
  readonly track: Track;
  readonly client: string;
  readonly primaryDimension: RubricDimension;
  readonly pressureContext: PressureContext;
}

export interface Shelf<C extends ShelfCase = ShelfCase> {
  readonly id: string;
  readonly title: string;
  readonly cases: readonly C[];
  readonly locked: boolean;
  /** Cases locked on an open shelf: the calibration cases behind the first one, until it is finished (AJ intake, 2026-09-24). */
  readonly lockedSlugs: readonly string[];
}

export interface ShelfInput<C extends ShelfCase> {
  readonly cases: readonly C[];
  readonly unlocked: boolean;
  /** The other calibration cases are open: the first case is finished. */
  readonly calibrationOpen: boolean;
  /** The one case a new learner is handed; null while the shelf state is still arriving. */
  readonly firstCase: string | null;
  /** The dimension the learner's record says is furthest behind, or null before there is a record. */
  readonly lagging: RubricDimension | null;
  /** The dimensions the plan's next steps name, in the plan's order. */
  readonly planDimensions: readonly RubricDimension[];
  readonly completedSlugs: readonly string[];
}

/**
 * THE LOCKED ROWS' ONE SENTENCE, in words, from what is still open (Law 6:
 * never a door that will not open without saying why). The conditions
 * already met are dropped, so the sentence shortens as the learner goes.
 */
export function lockedMessageFor(gate: { readonly firstCaseDone: boolean; readonly signedIn: boolean; readonly planAcknowledged: boolean }, firstCaseTitle: string): string {
  const needs: string[] = [];
  if (!gate.firstCaseDone) needs.push(LIBRARY.lockedNeeds.firstCase.replace("{case}", firstCaseTitle));
  if (!gate.planAcknowledged) needs.push(LIBRARY.lockedNeeds.plan);
  if (!gate.signedIn) needs.push(LIBRARY.lockedNeeds.signIn);
  if (needs.length === 0) return LIBRARY.lockedLead;
  const list = needs.length === 1 ? needs[0] : needs.length === 2 ? `${needs[0] ?? ""} and ${needs[1] ?? ""}` : `${needs.slice(0, -1).join(", ")}, and ${needs[needs.length - 1] ?? ""}`;
  return `${LIBRARY.lockedLead} ${list ?? ""}.`;
}

/** The pressure contexts, grouped by what kind of thing is bearing down. */
const STAKES: readonly { readonly id: string; readonly title: string; readonly contexts: readonly PressureContext[] }[] = [
  { id: "stakes-money", title: "Money on the line", contexts: ["REFINANCING", "SALE_PROCESS", "ACQUIRER_DILIGENCE", "ACTIVIST_INVESTOR", "TURNAROUND_LENDER"] },
  { id: "stakes-outside", title: "The outside world", contexts: ["REGULATOR", "LITIGATION", "PUBLIC_INCIDENT"] },
  { id: "stakes-people", title: "People and power", contexts: ["FAMILY_SUCCESSION", "NEW_LEADER", "CUSTOMER_ULTIMATUM"] },
];

function titleCase(label: string): string {
  return label.replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

export function trackLabel(track: Track): string {
  const found = TRACKS.find((t) => t.key === track);
  return found === undefined ? track.toLowerCase().replaceAll("_", " ") : found.label.replace(/^\d+\.\s*/, "").toLowerCase();
}

export function goalLabel(context: PressureContext): string {
  return GOAL_LABEL[context] ?? context.toLowerCase().replaceAll("_", " ");
}

export function skillLabel(dimension: RubricDimension): string {
  return SKILL_LABEL[dimension] ?? dimension.toLowerCase().replaceAll("_", " ");
}

/** The coach's picks: the lagging dimension's cases first, then the plan's, none already finished, none twice. */
export function curatedCases<C extends ShelfCase>(input: ShelfInput<C>): readonly C[] {
  const wanted: RubricDimension[] = [];
  for (const dim of [...(input.lagging === null ? [] : [input.lagging]), ...input.planDimensions]) {
    if (!wanted.includes(dim)) wanted.push(dim);
  }
  const done = new Set(input.completedSlugs);
  const picked: C[] = [];
  for (const dim of wanted) {
    for (const c of input.cases) {
      if (c.primaryDimension === dim && !done.has(c.slug) && !picked.includes(c)) picked.push(c);
    }
  }
  return picked;
}

export function buildShelves<C extends ShelfCase>(input: ShelfInput<C>): readonly Shelf<C>[] {
  const shelves: Shelf<C>[] = [];
  const calibration = input.cases.filter((c) => c.track === "CALIBRATION");
  const locked = !input.unlocked;
  // The calibration row is open, but until the first case is finished every
  // other case on it is locked: a new learner has one door.
  const lockedCalibration = input.unlocked || input.calibrationOpen ? [] : calibration.filter((c) => c.slug !== input.firstCase).map((c) => c.slug);

  if (input.unlocked) {
    const curated = curatedCases(input);
    if (curated.length > 0) shelves.push({ id: "curated", title: LIBRARY.curatedShelf, cases: curated, locked: false, lockedSlugs: [] });
  }
  shelves.push({ id: "calibration", title: LIBRARY.calibrationShelf, cases: calibration, locked: false, lockedSlugs: lockedCalibration });

  for (const track of TRACKS) {
    if (track.key === "CALIBRATION") continue;
    const cases = input.cases.filter((c) => c.track === track.key);
    if (cases.length > 0) shelves.push({ id: `track-${track.key.toLowerCase()}`, title: titleCase(trackLabel(track.key)), cases, locked, lockedSlugs: [] });
  }

  const dimensions: readonly RubricDimension[] = ["STRUCTURE", "QUANTITATIVE", "JUDGMENT", "COMMUNICATION", "AMBIGUITY_TOLERANCE", "CALIBRATION"];
  for (const dim of dimensions) {
    const cases = input.cases.filter((c) => c.primaryDimension === dim);
    if (cases.length > 0) shelves.push({ id: `skill-${dim.toLowerCase()}`, title: titleCase(skillLabel(dim)), cases, locked, lockedSlugs: [] });
  }

  for (const stake of STAKES) {
    const cases = input.cases.filter((c) => stake.contexts.includes(c.pressureContext));
    if (cases.length > 0) shelves.push({ id: stake.id, title: stake.title, cases, locked, lockedSlugs: [] });
  }

  return shelves;
}
